import { useMemo } from 'react';
import type { GridPhase, GridSlot } from '../../../domain/calculators/installments';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { MonthCell } from './MonthCell';
import { getYearMilestone, groupSlotsByYear } from './installmentUtils';
import './installments.css';

export interface AnnualBlueprintViewProps {
  slots: GridSlot[];
  activeFilter?: 'ALL' | GridPhase;
  highlightedMonth?: number | null;
  onSelect?: (slot: GridSlot) => void;
  onHover?: (slot: GridSlot) => void;
  onLeave?: () => void;
}

const MONTH_HEADERS = ['M01', 'M02', 'M03', 'M04', 'M05', 'M06', 'M07', 'M08', 'M09', 'M10', 'M11', 'M12'];

export function AnnualBlueprintView({
  slots,
  activeFilter = 'ALL',
  highlightedMonth,
  onSelect,
  onHover,
  onLeave,
}: AnnualBlueprintViewProps) {
  const { t } = useTranslation();
  const years = useMemo(() => groupSlotsByYear(slots), [slots]);

  return (
    <div className="inst-blueprint-wrap">
      {/* Month column headers visible on desktop */}
      <div className="inst-blueprint-col-header" aria-hidden="true">
        <div className="inst-year-col-spacer">{t('installments.tenureYearHeader')}</div>
        <div className="inst-month-cols-grid">
          {MONTH_HEADERS.map((h) => (
            <span key={h} className="inst-col-head-label">
              {h}
            </span>
          ))}
        </div>
      </div>

      <div className="inst-blueprint-rows">
        {years.map((year) => {
          const totalInYear = year.slots.length;
          const clearedInYear = year.clearedCount + year.advanceCount;
          const yearPct = totalInYear > 0 ? Math.round((clearedInYear / totalInYear) * 100) : 0;
          const startMonth = (year.yearNumber - 1) * 12 + 1;
          const endMonth = year.yearNumber * 12;
          const milestone = getYearMilestone(year.yearNumber);

          return (
            <div
              key={year.yearNumber}
              className={`inst-year-row ${year.isComplete ? 'is-complete' : ''} ${
                year.isActiveYear ? 'is-active-year' : ''
              } ${milestone ? 'has-milestone' : ''}`}
            >
              {/* Left Annual Rail */}
              <div className="inst-year-rail">
                <div className="inst-year-badge-top">
                  <span className="inst-year-title">
                    {t('installments.yearShort', { year: year.yearNumber })}
                  </span>
                  <span className="inst-year-range" style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    M{String(startMonth).padStart(2, '0')}–M{String(endMonth).padStart(2, '0')}
                  </span>
                </div>

                {milestone && (
                  <span
                    className="inst-year-milestone-pill"
                    data-testid={`grid-milestone-y${year.yearNumber}`}
                    title={t(milestone.labelKey)}
                  >
                    ★ {t(milestone.labelKey)}
                  </span>
                )}

                <div className="inst-year-progress-tag">
                  {year.isComplete ? (
                    <span className="inst-year-status is-done">
                      ✓ {totalInYear}/{totalInYear} {t('installments.settled')}
                    </span>
                  ) : year.isActiveYear ? (
                    <span className="inst-year-status is-active">
                      ⚡ {clearedInYear}/{totalInYear} ({yearPct}%)
                    </span>
                  ) : (
                    <span className="inst-year-status is-future">
                      0/{totalInYear} {t('installments.upcoming')}
                    </span>
                  )}
                </div>
              </div>

              {/* 12 Month Slots */}
              <div className="inst-year-cells-grid">
                {year.slots.map((slot) => {
                  const isDimmed = activeFilter !== 'ALL' && slot.phase !== activeFilter;
                  const isHighlighted = highlightedMonth === slot.installmentNumber;

                  return (
                    <MonthCell
                      key={slot.index}
                      slot={slot}
                      isDimmed={isDimmed}
                      isHighlighted={isHighlighted}
                      onSelect={onSelect}
                      onHover={onHover}
                      onLeave={onLeave}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AnnualBlueprintView;
